import { Accordion } from '@zoopla-eng/zds/accordion';
import { Box } from '@zoopla-eng/zds/box';
import { Button } from '@zoopla-eng/zds/button';
import { Divider } from '@zoopla-eng/zds/divider';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { Added, AddedToSite } from './AddedToSite';
import { Bedrooms, BedsMax, BedsMin } from './Bedrooms';
import { Features } from './Features';
import {
    accordionAndKeywordsStyle,
    accordionContainerStyle,
    buttonContainerStyle
} from './Filters.styles.css';
import {
    HomeType,
    IsAuction,
    IsRetirementHome,
    IsSharedOwnership,
    NewHomes
} from './HomeType';
import { Keywords } from './Keywords';
import { Price, PriceFrequency, PriceMax, PriceMin } from './Price';
import {
    BuyerIncentive,
    ChainFree,
    IncludeSold,
    PropertyStatus,
    ReducedPrice
} from './PropertyStatus';
import { PropertySubType, PropertyType } from './PropertyType';
import { buttonWrapperStyle } from './ToRentFilters.styles.css';
import { trackFilterClick } from './utils/';
import {
    getDefaultForSaleFilters,
    getForSaleFilters
} from './utils/getForSaleFilters';

const getSearchPath = (
    propertyType: PropertyType | 'retirement' | 'bungalow',
    searchPath: string | string[]
) => {
    const searchPaths = {
        property: 'property',
        houses: 'houses',
        flats: 'flats',
        farms_land: 'property',
        retirement: 'retirement',
        bungalow: 'bungalows'
    };

    if (!searchPath || typeof searchPath === 'string') {
        return [searchPaths[propertyType]];
    }

    // there is a bug in LSRP and perhaps Next itself where pushing to the router (and triggering GIP) causes the url params to appear as a path part in router.query['search-path'] i.e ['property', 'london', '?q=london']
    const validSearchPath = searchPath.filter((v) => !v.startsWith('?'));

    return [searchPaths[propertyType], ...validSearchPath.slice(1)];
};

type ForSaleFiltersProps = {
    onClose: () => void;
    onUpdate: () => void;
};

export const ForSaleFilters = ({ onClose, onUpdate }: ForSaleFiltersProps) => {
    const { query, push, pathname: currentPageRouteFilePath } = useRouter();

    const [isLoading, setIsLoading] = useState(false);

    const [accordionValue, updateAccordionValue] = useState<string[]>([]);

    const [formValue, updateFormValue] = useState(
        getForSaleFilters(query, currentPageRouteFilePath)
    );

    const onSubmit = async (e: { preventDefault: () => void }) => {
        setIsLoading(true);

        e.preventDefault();

        const updatedQuery = {
            'search-path': '', // keep typescript happy
            ...query,
            ...formValue,
            pn: ''
        };

        const searchType =
            formValue['new_homes'] === 'only' ? 'new-homes' : 'for-sale';

        let updatedSearchPath = getSearchPath(
            formValue['property_type'] as PropertyType,
            query['search-path'] ?? ''
        );

        if (
            formValue['is_retirement_home'] === 'true' &&
            formValue['property_type'] === 'property'
        ) {
            delete (updatedQuery as Partial<typeof updatedQuery>)[
                'is_retirement_home'
            ];

            updatedSearchPath = getSearchPath('retirement', updatedSearchPath);
        }

        if (formValue['new_homes'] !== 'exclude') {
            delete (updatedQuery as Partial<typeof updatedQuery>)['new_homes'];
        }

        if (
            formValue['property_sub_type'].length === 1 &&
            formValue['property_sub_type'][0] === 'bungalow'
        ) {
            updatedSearchPath = getSearchPath('bungalow', updatedSearchPath);
        }

        if (formValue['property_type'] !== 'farms_land') {
            delete (updatedQuery as Partial<typeof updatedQuery>)[
                'property_type'
            ];
        }

        Object.keys(updatedQuery).forEach((key) => {
            if (updatedQuery[key as keyof typeof updatedQuery] === '') {
                delete updatedQuery[key as keyof typeof updatedQuery];
            }
        });

        // 'search-path' is the name of the dynamic route parameter and so we should not apply as a genuine query param below
        const { 'search-path': invalid, ...validParams } = updatedQuery;

        const pathname = currentPageRouteFilePath.includes('/map/')
            ? [searchType, 'map', ...updatedSearchPath]
            : [searchType, ...updatedSearchPath];

        const updatedPathname = `/${pathname.join('/')}/`;

        await push({
            pathname: updatedPathname,
            query: {
                ...validParams
            }
        });

        setIsLoading(false);

        onUpdate();
    };

    return (
        <Box display="flex" flexDirection="column" rowGap="S500" height="100%">
            <Box display="flex" justifyContent="center">
                <Button
                    category="tertiary"
                    onClick={() => {
                        trackFilterClick();
                        updateFormValue(getDefaultForSaleFilters());
                    }}
                >
                    Reset
                </Button>
            </Box>
            <Divider direction="horizontal" />
            <div className={accordionAndKeywordsStyle}>
                <div className={accordionContainerStyle}>
                    <Accordion
                        value={accordionValue}
                        panels={[
                            {
                                label: 'Bedrooms',
                                value: 'bedrooms',
                                component: (
                                    <Bedrooms
                                        value={{
                                            bedsMin:
                                                formValue.beds_min as BedsMin,
                                            bedsMax:
                                                formValue.beds_max as BedsMax
                                        }}
                                        onChange={({ bedsMin, bedsMax }) => {
                                            trackFilterClick();
                                            updateFormValue((oldFormValue) => ({
                                                ...oldFormValue,
                                                beds_min: bedsMin,
                                                beds_max: bedsMax
                                            }));
                                        }}
                                    />
                                )
                            },
                            {
                                label: 'Price',
                                value: 'price',
                                component: (
                                    <Price
                                        value={{
                                            priceMin:
                                                formValue.price_min as PriceMin,
                                            priceMax:
                                                formValue.price_max as PriceMax,
                                            priceFrequency:
                                                formValue.price_frequency as PriceFrequency
                                        }}
                                        onChange={({
                                            priceMin,
                                            priceMax,
                                            priceFrequency
                                        }) => {
                                            trackFilterClick();
                                            updateFormValue((oldFormValue) => ({
                                                ...oldFormValue,
                                                price_min: priceMin,
                                                price_max: priceMax,
                                                price_frequency: priceFrequency
                                            }));
                                        }}
                                    />
                                )
                            },
                            {
                                label: 'Property type',
                                value: 'property_type',
                                component: (
                                    <PropertyType
                                        value={{
                                            propertyType:
                                                formValue.property_type as PropertyType,
                                            propertySubType:
                                                formValue.property_sub_type as PropertySubType
                                        }}
                                        onChange={({
                                            propertySubType,
                                            propertyType
                                        }) => {
                                            trackFilterClick();
                                            updateFormValue((oldFormValue) => ({
                                                ...oldFormValue,
                                                property_sub_type:
                                                    propertySubType,
                                                property_type: propertyType
                                            }));
                                        }}
                                    />
                                )
                            },
                            {
                                label: 'Include, exclude & show only',
                                value: 'home_type',
                                component: (
                                    <HomeType
                                        value={{
                                            newHomes:
                                                formValue.new_homes as NewHomes,
                                            isRetirementHome:
                                                formValue.is_retirement_home as IsRetirementHome,
                                            isSharedOwnership:
                                                formValue.is_shared_ownership as IsSharedOwnership,
                                            isAuction:
                                                formValue.is_auction as IsAuction
                                        }}
                                        onChange={({
                                            newHomes,
                                            isRetirementHome,
                                            isSharedOwnership,
                                            isAuction
                                        }) => {
                                            trackFilterClick();
                                            updateFormValue((oldFormValue) => ({
                                                ...oldFormValue,
                                                new_homes: newHomes,
                                                is_retirement_home:
                                                    isRetirementHome,
                                                is_shared_ownership:
                                                    isSharedOwnership,
                                                is_auction: isAuction
                                            }));
                                        }}
                                    />
                                )
                            },
                            {
                                label: 'Must-haves',
                                value: 'must_haves',
                                component: (
                                    <Features
                                        value={formValue.feature as Features}
                                        onChange={(feature) => {
                                            trackFilterClick();
                                            updateFormValue((oldFormValue) => ({
                                                ...oldFormValue,
                                                feature: feature
                                            }));
                                        }}
                                    />
                                )
                            },
                            {
                                label: 'Property status',
                                value: 'property_status',
                                component: (
                                    <PropertyStatus
                                        value={{
                                            buyerIncentive:
                                                formValue.buyer_incentive as BuyerIncentive,
                                            chainFree:
                                                formValue.chain_free as ChainFree,
                                            reducedPrice:
                                                formValue.reduced_price_only as ReducedPrice,
                                            includeSold:
                                                formValue.include_sold as IncludeSold
                                        }}
                                        onChange={({
                                            buyerIncentive,
                                            chainFree,
                                            reducedPrice,
                                            includeSold
                                        }) => {
                                            trackFilterClick();
                                            updateFormValue((oldFormValue) => ({
                                                ...oldFormValue,
                                                buyer_incentive: buyerIncentive,
                                                chain_free: chainFree,
                                                reduced_price_only:
                                                    reducedPrice,
                                                include_sold: includeSold
                                            }));
                                        }}
                                    />
                                )
                            },
                            {
                                label: 'Added to site',
                                value: 'added',
                                component: (
                                    <AddedToSite
                                        value={formValue.added as Added}
                                        onChange={(added: Added) => {
                                            trackFilterClick();
                                            updateFormValue((oldFormValue) => ({
                                                ...oldFormValue,
                                                added: added
                                            }));
                                        }}
                                    />
                                )
                            }
                        ]}
                        onValueChange={updateAccordionValue}
                    />
                </div>
                <Box px="S400" bpM100={{ px: 'L400' }}>
                    <Keywords
                        value={formValue.keywords as Keywords}
                        onChange={(keywords) => {
                            trackFilterClick();
                            updateFormValue((oldFormValue) => ({
                                ...oldFormValue,
                                keywords: keywords
                            }));
                        }}
                    />
                </Box>
            </div>
            <div className={buttonWrapperStyle}>
                <div className={buttonContainerStyle}>
                    <Button
                        disabled={isLoading}
                        category="primary"
                        onClick={onSubmit}
                    >
                        Update Results
                    </Button>
                </div>
            </div>
        </Box>
    );
};
